import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * נקודות, פרסים ותחרויות (PRD 5.8).
 *
 * היתרה היא תמיד סכום הפנקס ולא מספר שנשמר בנפרד. לכן אין כאן שום
 * חישוב בצד הלקוח: מה שהשרת מחזיר הוא המצב.
 */

export type PointsEntry = {
  id: string;
  points: number;
  reason: string;
  created_at: string;
  granted_by: string | null;
};

export type MyPoints = {
  balance: number;
  earned_this_month: number;
  history: PointsEntry[];
};

export type Reward = {
  id: string;
  name: string;
  description: string | null;
  cost_points: number;
  image_path: string | null;
  stock: number | null;
  is_active: boolean;
};

export type Redemption = {
  id: string;
  reward_id: string;
  employee_id: string;
  status: 'requested' | 'approved' | 'delivered' | 'rejected';
  created_at: string;
  rewards: { name: string; cost_points: number } | null;
  employees: { first_name: string; last_name: string } | null;
};

export type Competition = {
  id: string;
  title: string;
  description: string | null;
  starts_at: string;
  ends_at: string;
  scope: 'individual' | 'branch';
  prize: string | null;
  winner_employee_id: string | null;
  winner_branch_id: string | null;
};

export type Standings = {
  scope: 'individual' | 'branch';
  starts_at: string;
  ends_at: string;
  is_open: boolean;
  employees: { employee_id: string; name: string; branch: string | null; points: number }[];
  branches: { branch_id: string; name: string; points: number }[];
};

export type LeaderRow = {
  employee_id: string;
  full_name: string;
  branch_id: string | null;
  points: number;
};

export type Celebrations = {
  birthdays: { employee_id: string; name: string; date: string }[];
  anniversaries: { employee_id: string; name: string; date: string; years: number }[];
};

export type CurrentAward = {
  award_id: string;
  type: string;
  icon: string | null;
  name: string;
  reason: string | null;
  image_path: string | null;
  month: string;
};

export const GAMES_KEY = ['games'] as const;

export function useMyPoints() {
  return useQuery({
    queryKey: [...GAMES_KEY, 'my-points'],
    queryFn: async (): Promise<MyPoints> => {
      const { data, error } = await supabase.rpc('my_points');
      if (error) throw error;
      return (data as MyPoints | null) ?? { balance: 0, earned_this_month: 0, history: [] };
    },
  });
}

export function useRewards() {
  return useQuery({
    queryKey: [...GAMES_KEY, 'rewards'],
    queryFn: async (): Promise<Reward[]> => {
      const { data, error } = await supabase
        .from('rewards')
        .select('*')
        .eq('is_active', true)
        .order('cost_points');
      if (error) throw error;
      return (data ?? []) as Reward[];
    },
  });
}

/** הבקשות של העובד המחובר. ה RLS כבר מצמצם לשלו. */
export function useMyRedemptions() {
  return useQuery({
    queryKey: [...GAMES_KEY, 'my-redemptions'],
    queryFn: async (): Promise<Redemption[]> => {
      const { data, error } = await supabase
        .from('reward_redemptions')
        .select('id, reward_id, employee_id, status, created_at, rewards ( name, cost_points )')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Redemption[];
    },
  });
}

export function useRedeem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (rewardId: string) => {
      const { error } = await supabase.rpc('redeem_reward', { p_reward: rewardId });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: GAMES_KEY });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/**
 * טבלת המובילים לחודש הנוכחי.
 *
 * הטווח מחושב כאן ולא בשרת, כי אותה פונקציה משרתת גם תצוגות אחרות
 * (שבוע, תחרות). הגבול העליון הוא תחילת החודש הבא, והשוואה היא
 * "קטן מ", ולכן היום האחרון בחודש נספר במלואו.
 */
export function useLeaderboard(branchId?: string | null) {
  const from = new Date();
  from.setDate(1);
  from.setHours(0, 0, 0, 0);
  const to = new Date(from);
  to.setMonth(to.getMonth() + 1);

  return useQuery({
    queryKey: [...GAMES_KEY, 'leaderboard', from.toISOString(), branchId ?? 'all'],
    queryFn: async (): Promise<LeaderRow[]> => {
      const { data, error } = await supabase.rpc('leaderboard', {
        p_from: from.toISOString(),
        p_to: to.toISOString(),
        p_branch: branchId ?? undefined,
      });
      if (error) throw error;
      return (data ?? []) as LeaderRow[];
    },
    staleTime: 30 * 1000,
  });
}

export function useCompetitions() {
  return useQuery({
    queryKey: [...GAMES_KEY, 'competitions'],
    queryFn: async (): Promise<Competition[]> => {
      const { data, error } = await supabase
        .from('competitions')
        .select('*')
        .order('ends_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as Competition[];
    },
  });
}

export function useStandings(competitionId: string | null) {
  return useQuery({
    queryKey: [...GAMES_KEY, 'standings', competitionId],
    enabled: Boolean(competitionId),
    queryFn: async (): Promise<Standings | null> => {
      const { data, error } = await supabase.rpc('competition_standings', {
        p_competition: competitionId as string,
      });
      if (error) throw error;
      return data as Standings | null;
    },
    staleTime: 30 * 1000,
  });
}

export function useCelebrations() {
  return useQuery({
    queryKey: [...GAMES_KEY, 'celebrations'],
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<Celebrations> => {
      const { data, error } = await supabase.rpc('celebrations', { p_days: 14 });
      if (error) throw error;
      return (data as Celebrations | null) ?? { birthdays: [], anniversaries: [] };
    },
  });
}

export function useCurrentAwards() {
  return useQuery({
    queryKey: [...GAMES_KEY, 'current-awards'],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<CurrentAward[]> => {
      const { data, error } = await supabase.rpc('current_awards');
      if (error) throw error;
      return (data as CurrentAward[] | null) ?? [];
    },
  });
}

export const redemptionStatusLabel: Record<Redemption['status'], string> = {
  requested: 'ממתינה לאישור',
  approved: 'אושרה',
  delivered: 'נמסר',
  rejected: 'נדחתה',
};

/** האם לעובד יש מספיק נקודות, ומה חסר. */
export function affordability(
  reward: Reward,
  balance: number,
): { canAfford: boolean; missing: number } {
  const missing = Math.max(0, reward.cost_points - balance);
  return { canAfford: missing === 0, missing };
}

/** "עוד 3 ימים", "היום", "מחר" — לתאריכי חגיגה. */
export function daysLabel(days: number): string {
  if (days <= 0) return 'היום';
  if (days === 1) return 'מחר';
  return `בעוד ${days} ימים`;
}
